import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { AUTH } from '../constants/testIds';

const DEMO_CREDENTIALS = [
  { role: 'Master Admin', email: 'superadmin@eduva.com', password: 'EduvaAdmin2024!' },
  { role: 'School Admin', email: 'admin@greenfield.edu.ng', password: 'School@2024' },
  { role: 'Teacher', email: 'teacher1@greenfield.edu.ng', password: 'Teacher@2024' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const getRedirectPath = (role) => {
    const paths = { master_admin: '/master/dashboard', school_owner: '/school/dashboard', school_admin: '/school/dashboard', teacher: '/teacher/dashboard', parent: '/parent/dashboard', student: '/student/dashboard' };
    return paths[role] || '/';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter your email and password'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      login(res.token, res.user, res.school, res.subscription_status, res.grace_days_remaining);
      toast.success(`Welcome back, ${res.user.first_name}!`);
      navigate(getRedirectPath(res.user.role));
    } catch (err) {
      toast.error(err?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (cred) => {
    setEmail(cred.email);
    setPassword(cred.password);
    setShowDemo(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-white to-[#eef1ff] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F1623] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#516EFA]/20 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#516EFA] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-2xl text-white">Eduva</span>
          </div>
          <div>
            <h2 className="font-outfit text-4xl font-bold text-white mb-4 leading-tight">
              The Digital OS<br />for Your School
            </h2>
            <p className="text-slate-400 text-lg mb-8 leading-relaxed">
              AI-powered school management that helps you work smarter, not harder.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "500+", label: "Schools" },
                { value: "50K+", label: "Students" },
                { value: "AI", label: "Powered" },
                { value: "99.9%", label: "Uptime" },
              ].map((s, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="font-outfit font-bold text-2xl text-white">{s.value}</p>
                  <p className="text-slate-400 text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="text-slate-500 text-sm">© 2024 Eduva • myeduva.com</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-[#516EFA] flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-slate-900">Eduva</span>
          </div>

          <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-2">Welcome back</h1>
          <p className="text-slate-500 mb-8">Sign in to your school management portal</p>

          <form onSubmit={handleSubmit} data-testid={AUTH.loginForm} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email or Username</Label>
              <Input id="email" type="text" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address or student username" data-testid={AUTH.loginEmail}
                className="h-11 border-slate-200 focus:border-[#516EFA] focus:ring-[#516EFA]" />
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <button type="button" className="text-xs text-[#516EFA] hover:underline">Forgot password?</button>
              </div>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" data-testid={AUTH.loginPassword}
                  className="h-11 pr-10 border-slate-200 focus:border-[#516EFA] focus:ring-[#516EFA]" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid={AUTH.loginSubmit}
              className="w-full h-11 bg-[#516EFA] hover:bg-[#4058e8] text-white font-semibold rounded-xl">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : 'Sign In'}
            </Button>
          </form>

          {/* Demo Credentials */}
           

          <p className="mt-6 text-center text-sm text-slate-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#516EFA] font-semibold hover:underline">Register your school</Link>
          </p>

          <p className="mt-4 text-center text-xs text-slate-400">
            <a href="https://www.myeduva.com" className="hover:text-slate-600 transition-colors">← Back to Home</a>
          </p>
        </div>
      </div>
    </div>
  );
}
